import {
    AfterViewInit,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatPaginator} from "@angular/material/paginator";
import {MatTabChangeEvent} from "@angular/material/tabs";
import {HeaderDataService} from "../../services/header_data.service";
import {ApiService} from "../../services/api/api.service";
import {HandsontableGeneratorService, HOTLookup} from "../../services/handsontable-generator.service";
import {HotInstance} from "../../services/hot-instance";
import {PaginationDataSource} from "../../services/api/pagination-data-source";
import {SearchQueryOptions} from "../../services/api/search-query-options";
import {EMPTY, firstValueFrom, forkJoin, Observable, of, Subject, throwError} from "rxjs";
import {catchError, concatMap, debounceTime, finalize, first, map, switchMap, take, tap} from "rxjs/operators";
import * as utils from "../../lib/utils";
import {ListResponse} from "../../services/api/response-types";
import {getBaseFilter, getRelationWithManyIdsFilter} from "../../services/api/filter_utils";
import {SeriesLight} from "../../_models/series";
import {Account} from "../../_models/account";
import {AppScope} from "../../services/app_scope.service";
import {AccountService} from "../../services/account.service";
import {PivotViewComponent} from "../pivot-view/pivot-view.component";
import {HistoryTableComponent} from "../history-table/history-table.component";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {UtilsBarComponent} from "../../tables/utils-bar/utils.bar.component";
import {AccountFilterComponent} from "../../components/account-filter/account-filter.component";
import {NotificationService} from "../../services/notification.service";
import {fetchAllPages} from "../../services/api/fetch-all-pages";
import {exportHandsontableToXlsx} from "../handsontable-to-xlsx";
import {PopupHandle, PopupService} from "../../services/popup-service";
import {
    SheetDownloadFormatMenuComponent,
    SHEET_DOWNLOAD_OPTIONS
} from "../../forms/custom-dialog/sheet-download-format-menu/sheet-download-format-menu.component";

type PendingPasteLookup = { text: string; token: number };
type SeriesQuery = {
    query: string;
    process: (names: string[]) => void;
    exactNameOnly?: boolean;
    cachedNames?: string[];
};
type PasteRange = { startRow: number; startCol: number; endRow: number; endCol: number };
type DownloadPageSnapshot = { mappers: any[] };
// Minimal row shape for the exhaustive download/pivot fetch. fetchAllPages needs
// `id` for its cross-page dedup; attributes/relationships are what formatMapperJson
// and fetchMissingSeries read. Typing the call sites (instead of <any>) keeps the
// dedup-by-id contract honest.
type MapperRow = { id: string; attributes?: any; relationships?: any };

@Component({
    selector: 'collector-mapper-sheet-view',
    imports: [
        SharedModule,
        PivotViewComponent,
        HistoryTableComponent,
        UtilsBarComponent,
        AccountFilterComponent,
    ],
    templateUrl: './collector-mapper-sheet-view.component.html',
    encapsulation: ViewEncapsulation.None
})
export class CollectorMapperSheetViewComponent implements OnInit, AfterViewInit, OnDestroy {
    // Cap on Series-bearing pastes; PASTE_LOOKUP_BATCH_SIZE also bounds the `in`
    // filter size for id/name lookups so the query string stays within URL limits.
    private static readonly PASTE_ROW_LIMIT = 1000;
    private static readonly PASTE_LOOKUP_BATCH_SIZE = 100;
    // Stable, user-visible Name order shared by the paginated table load
    // (pageMappers) and the full-dataset download fetch, so the exported file
    // matches the on-screen order. Keep a unique id tie-breaker last so offset pages
    // can't overlap or skip rows when source_name repeats or is null.
    private static readonly MAPPER_SORT = 'source_name,id';

    private readonly destroyRef = inject(DestroyRef);
    private readonly seriesQuery$ = new Subject<SeriesQuery>();
    @ViewChild('hot_anchor') hot_anchor: ElementRef;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    hot: HotInstance;
    title: string;
    search: string;
    schema: any;
    column_list: any;

    collectors: any[];
    mappers: any[];
    mapper_aggregation_methods: string[] = ['total', 'mean', 'latest_value', 'difference', 'difference_excluding_negatives',
        'mean_excluding_zeros', 'mean_excluding_negatives', 'mean_negatives_to_zero', 'mean_greater_than_1',
        'time_weighted_average', 'time_weighted_average_negatives_to_zero'];
    // Series referenced by the loaded mappers, fetched incrementally (per mapper
    // page, and in bulk before download/pivot) rather than pre-loading the whole
    // catalogue. seriesLookups mirrors this for HOT's autocomplete/accessor.
    series_light: any[];
    seriesLookups: HOTLookup;
    users: any[];
    totalRowCount: number;

    // Paste-by-name state for the Series column (mirrors series-component-sheet-view).
    private pasteLookupToken = 0;
    private readonly pendingSeriesPastes = new WeakMap<any, PendingPasteLookup>();
    private readonly pasteResolvingValues = new Set<string>();
    private pasteInFlight = false;
    // Stash for a paste whose rows Handsontable hasn't created yet. HOT validates
    // asynchronously (validateCell wraps the validator in setTimeout), so a paste
    // touching a validated column (Series) creates its overflow rows in a later
    // macrotask — after afterPaste. We capture the paste here and resolve it from
    // the CopyPaste.paste afterChange, which fires once the rows exist.
    private pendingPasteContext: {
        pasted: any[][];
        config: PasteRange;
    } | null = null;

    dataSource: PaginationDataSource<any>;
    page_size: number = 100;
    page_size_options: number[] = [10, 20, 50, 100];
    // Server-side "filter table by" account selection; composed with the search
    // into the dataSource filters (buildCurrentFilters).
    accountFilterIds: string[] = [];
    loading: boolean = true;

    // Total rows for the current filter, populated from response.meta.count.
    totalCount: number | null = null;
    firstPageReady: boolean = false;
    downloadInProgress: boolean = false;
    fullDatasetLoaded: boolean = false;
    // True when the most recent pivot load FAILED. The template uses it to show an
    // explicit error state instead of rendering <pivot-view> over stale/empty pivotData
    // (a failed refresh must not look like a successful one).
    pivotLoadFailed: boolean = false;
    // Monotonic token for pivot loads. Re-entering the Pivot tab starts a fresh load
    // without cancelling the prior one (only component destruction stops them); without
    // this guard a slow earlier load landing after a newer one could clobber the newer
    // result (e.g. a stale failure flipping pivotLoadFailed over a fresh success). Only
    // the load whose seq still matches pivotLoadSeq may write pivotData/state.
    private pivotLoadSeq = 0;
    pivotData: any[] = [];
    pivotExtraColumns = [
        // Account isn't a table column but is meaningful for pivot grouping/filtering.
        // The Pivot tab on Collector Mappers is rarely exercised in practice, so we
        // intentionally don't derive account_name from the account relationship +
        // an AppScope lookup when the backend serializer omits it — the cell falls
        // back to '' rather than us building extra plumbing for a near-unused path.
        {title: 'Account', accessor: (item: any) => item?.attributes?.account_name ?? ''}
    ];
    private isDestroyed = false;
    // The open download format-picker overlay, so ngOnDestroy can tear it down — it
    // lives in the CDK overlay container (app root), not this component's view, so it
    // isn't disposed when the component is destroyed.
    private downloadPopupHandle: PopupHandle<SheetDownloadFormatMenuComponent> | null = null;
    private mapperReloadScheduled = false;
    // Set when a $page emission arrives during a download window (onPage skips the
    // grid update so the bulk-fetched dataset isn't repainted mid-export); the
    // download teardown paths re-fetch the current page when it's set.
    private pendingPageRefresh = false;
    // True only when the visible page is page 0 and the server returned a short
    // page for the live query. Unlike meta.count, that is a safe completion signal
    // for the download fast path.
    private currentPageIsCompleteDataset = false;
    // Set by onPage when it steps the paginator back off an empty page (a delete
    // emptied the last page): the corrected fetch is queued behind
    // PaginationDataSource's debounce, so pageMappers' finalize holds the spinner +
    // read-only lock until that fetch lands rather than briefly exposing the stale
    // previous page for editing. Cleared when the next pageMappers fetch starts.
    private navigatingToPrevPage = false;
    private prefetch$: Observable<unknown>;

    constructor(private headerData: HeaderDataService,
                private api: ApiService,
                private handsontableGenerator: HandsontableGeneratorService,
                private notification: NotificationService,
                private appScope: AppScope,
                private accountService: AccountService,
                private popupService: PopupService) {
        this.hot = new HotInstance(["attributes.source_name"]);

        // Drives the Series autocomplete: debounce typing, then search the server by
        // name (or name+description) and merge results into the lookups incrementally.
        this.seriesQuery$.pipe(
            debounceTime(300),
            switchMap(({query, process, exactNameOnly, cachedNames}) => {
                const opts = new SearchQueryOptions(100, 1, 'name');
                opts.filters = exactNameOnly
                    ? [getBaseFilter(query, 'name')]
                    : query
                        ? [{
                            or: [
                                {name: 'name', op: 'ilike', val: `%${query}%`},
                                {name: 'description', op: 'ilike', val: `%${query}%`},
                                // The Series cell renders `name-description`, but neither column
                                // holds that combined string — so typing/committing the exact
                                // visible value of an as-yet-unloaded series matches nothing above
                                // and the cell stays red + unsaveable. Resolve the same name
                                // candidates the paste path uses (split on "-") by exact name so
                                // the visible value resolves; resolveUnverifiedSeriesRows() (below)
                                // then repairs the committed cell.
                                {name: 'name', op: 'in', val: this.getNameCandidates(query)},
                            ]
                        }]
                        : [];
                return this.api.series_light.searchMany(opts).pipe(
                    map((response: ListResponse<SeriesLight>) => ({response, process, cachedNames: cachedNames ?? []})),
                    // Keep the outer subscription alive on HTTP errors — Model.searchMany
                    // rethrows via throwError, so without this a single 500/timeout would
                    // tear down seriesQuery$ for the rest of the session and HOT would
                    // silently never receive process() results again.
                    catchError(() => {
                        this.notification.openError('Series search failed. Please try again.');
                        process([]);
                        return EMPTY;
                    }),
                );
            }),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(({response, process, cachedNames}) => {
            const names = this.mergeSeriesIntoLookups(response.data);
            process(this.uniqueNames([...(cachedNames ?? []), ...names]).slice(0, 100));
            // A Series value typed and committed before this debounced search returned
            // was stashed as _unverifiedSeriesText (id=null) by seriesDataAccessor;
            // now that the search has merged the matching series, revisit those rows so
            // the cell resolves instead of staying red. The paste path self-repairs in
            // finalisePaste — this is the equivalent for the typed-edit path. Deferred
            // so process() can populate the open autocomplete dropdown first.
            setTimeout(() => this.resolveUnverifiedSeriesRows(), 0);
        });
    }

    ngOnInit(): void {
        this.title = 'Collector Mappers';

        // Register the header (title + Save/Download) SYNCHRONOUSLY so the global
        // header menu never shows the previous view's title/handlers while the first
        // page loads — or permanently, if a request below fails. HeaderDataService is
        // shared global state, so a late buildHeader() would let the menu invoke the
        // previous component's button handlers. The handlers are safe to click early:
        // download() no-ops until firstPageReady, and save() guards on the live
        // Handsontable instance.
        this.buildHeader();

        // Seed the table's account filter from active_account_id when it's already
        // known (fast path), then reconcile against AccountService.activeAccountChanged
        // once the first page is in (see reconcileInitialAccountFilter). The /view
        // route only waits for user context, not for AccountService's /api/ActiveAccount
        // request, so on a fresh load active_account_id can still be unset here. We do
        // NOT block the load on activeAccountChanged: it never emits when there's no
        // active account / the request fails (combineLatest + EMPTY), and in that case
        // <account-filter> also shows nothing selected, so the unfiltered table already
        // matches the selector. The grand-total label stays independent
        // (refreshTotalRowCount, unfiltered) — backend mapper reads are scoped to all
        // accessible accounts, not the X-Active-Account header.
        this.accountFilterIds = this.appScope.active_account_id ? [this.appScope.active_account_id] : [];

        // pageMappers resolves the series referenced by each page on the fly
        // (fetchMissingSeries), so series_light is populated for the first page by
        // the time genTable() builds the lookups — no full-catalogue preload.
        this.series_light = [];
        this.prefetch$ = forkJoin([
            this.api.collector.searchMany().pipe(tap(response => this.collectors = response.data)),
            this.api.users.searchMany().pipe(tap(response => this.users = response.data)),
        ]);

        // Grand total across all accessible accounts (independent of the active filter).
        this.refreshTotalRowCount();
    }

    ngAfterViewInit(): void {
        // The paginator @ViewChild only resolves after view init (its toolbar row is
        // rendered unconditionally in the template for exactly this reason), and
        // createDataSource() wires PaginationDataSource to it — so the first mapper
        // page is requested from here, not ngOnInit.
        this.prefetch$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => this.createDataSource(),
            error: (err) => {
                // Without this branch, an error in either prefetch request would leave
                // `loading=true` and the spinner up forever — there's no other path that
                // clears it. (A failed mapper page reports from pageMappers instead.)
                console.error('Failed to load Collector Mappers initial data', err);
                this.notification.openError('Failed to load Collector Mappers. Please refresh and try again.');
                this.loading = false;
            },
        });
    }

    createDataSource(): void {
        this.paginator.pageSize = this.page_size;
        this.paginator.pageIndex = 0;
        const initialQuery = new SearchQueryOptions();
        initialQuery.page_number = 1;
        initialQuery.sort = CollectorMapperSheetViewComponent.MAPPER_SORT;
        initialQuery.filters = this.buildCurrentFilters();
        this.dataSource = new PaginationDataSource<any>(
            (query) => this.pageMappers(query),
            initialQuery,
            this.paginator,
        );
        this.paginator.page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.markPageLoadPending());
        this.dataSource.$page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (response: ListResponse<any>) => this.onPage(response)
        );
    }

    /**
     * Single writer of the page-scoped state (mappers / totalCount / paginator.length)
     * and the grid contents. The endpoint (pageMappers) stays a pure fetch+enrich
     * pipeline, so an in-flight response superseded by PaginationDataSource's
     * switchMap can never half-apply state.
     */
    private onPage(response: ListResponse<any>): void {
        if (this.isDestroyed) {
            return;
        }
        // During a bulk download the grid temporarily holds the full dataset for the
        // exporters; an emission landing now must not repaint it (or overwrite
        // this.mappers/totalCount mid-export). Flag it and re-fetch once the download
        // window closes (executeDownload's finally / the picker-dismissal path).
        if (this.downloadInProgress) {
            this.pendingPageRefresh = true;
            return;
        }
        const count = response.meta?.count ?? response.data.length;
        this.totalCount = count;
        this.paginator.length = count;
        // Deleting rows from the last page (or a filter shrinking the total) can leave
        // the paginator beyond the new last page — jump straight to the last populated
        // page instead of rendering an empty grid. Use dataSource.goToPage(), NOT
        // paginator.lastPage(): lastPage() is guarded by hasNextPage() and no-ops once
        // pageIndex is already PAST the last page (exactly this overshoot case), which
        // would leave navigatingToPrevPage stuck true and the grid loading/readOnly
        // forever. goToPage() drives the query directly, in ONE fetch. The Math.min
        // clamp guarantees we step back at least one page even if a stale count still
        // points at this (now-empty) page, so we can't re-fetch the same empty page in a
        // loop. Don't commit the empty response to this.mappers here: the grid keeps
        // showing the previous page until the corrected fetch lands, so this.mappers
        // must stay aligned with what's displayed (anyone reading it mid-window would
        // otherwise see an empty dataset).
        if (!response.data.length && count > 0 && this.paginator.pageIndex > 0) {
            // Hold the loading lock across the debounced corrected fetch (see
            // navigatingToPrevPage) so the stale previous page isn't left editable.
            this.navigatingToPrevPage = true;
            this.currentPageIsCompleteDataset = false;
            const lastPageIndex = Math.ceil(count / this.paginator.pageSize) - 1;
            this.dataSource.goToPage(Math.max(0, Math.min(lastPageIndex, this.paginator.pageIndex - 1)));
            return;
        }
        this.currentPageIsCompleteDataset =
            this.paginator.pageIndex === 0 && response.data.length < this.paginator.pageSize;
        // The server snapshot for this page — kept distinct from the live grid (the
        // grid loads its own copy below) so readers like the download fast-path see
        // unedited rows.
        this.mappers = response.data;
        if (!this.hot?.instance) {
            this.genTable();
            this.firstPageReady = true;
            // If the active account wasn't known when the initial (unfiltered) load
            // went out, reconcile now from the SAME source <account-filter> reads
            // for its visible selection, so the server-side filter and the selector
            // can't diverge ("looks account-filtered but is showing all accounts").
            if (!this.accountFilterIds.length) {
                this.reconcileInitialAccountFilter();
            }
        } else {
            // Load a copy so the grid mutates its own data, leaving this.mappers as the
            // pristine server snapshot (mirrors installFullDataset). source_data tracks
            // the grid's copy.
            const pageData = utils.deepCopy(this.mappers);
            this.hot.instance.loadData(pageData);
            this.hot.source_data = pageData;
        }
        // A page load replaces every row, so prior edit tracking no longer applies.
        // Cleared here, on the success path, rather than before the async query, so
        // a failed reload can't leave edits visible-but-untracked. Safe ordering:
        // loadData's afterChange fires with source 'loadData', which the generator's
        // tracker ignores.
        this.hot.change_ids = [];
        this.hot.new_rows = [];
    }

    /**
     * Apply the active account as the table's account filter once it becomes known,
     * but only when the initial load went out unfiltered (active_account_id was unset
     * at ngOnInit) and the user hasn't since chosen their own filter. Driven by the
     * same AccountService.activeAccountChanged the <account-filter> selector reads, so
     * the server-side filter and the visible selection stay in lock-step. first()
     * reacts to the initial active account only — later changes are the table's own
     * concern. Never fires when activeAccountChanged doesn't emit (no active account /
     * failed request), leaving the table unfiltered, which matches the empty selector.
     */
    private reconcileInitialAccountFilter(): void {
        this.accountService.activeAccountChanged.pipe(
            first(),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe(({account_id}) => {
            if (this.isDestroyed || !account_id || !this.hot?.instance) {
                return;
            }
            // Don't override a filter the user set in the meantime, clobber unsaved edits
            // the filter reload would discard, or start a competing page query while a
            // bulk download is mid-flight (executeDownload runs its own fetchAllPages
            // independently of the dataSource).
            if (this.accountFilterIds?.length || this._hasUnsavedChanges() || this.downloadInProgress) {
                return;
            }
            this.accountFilterIds = [account_id];
            this.applyFilters();
        });
    }

    /**
     * PaginationDataSource endpoint: fetch one page for the given query and resolve
     * the series it references before handing the rows on, so the Series cells render
     * their names (not blank) as the page appears. Deliberately a pure fetch+enrich
     * pipeline — all state/grid writes live in onPage(), the single writer, and
     * PaginationDataSource's switchMap discards superseded in-flight pages.
     *
     * The grid is read-only-locked for the duration: loadData() replaces every row
     * when the page lands, so a keystroke mid-flight would be silently discarded
     * (HotInstance.query() gave replacement loads the same lock).
     */
    private pageMappers = (query: SearchQueryOptions): Observable<ListResponse<any>> => {
        // These lock side-effects intentionally sit in the function body, not deferred
        // into the returned Observable. PaginationDataSource pipes this endpoint through
        // switchMap, which (RxJS 7) unsubscribes the superseded in-flight fetch — running
        // ITS finalize, loading/readOnly -> false — BEFORE it calls this projection. So
        // setting them true here runs AFTER that teardown and holds for the new fetch;
        // deferring would be harmless but buys nothing.
        this.loading = true;
        this.currentPageIsCompleteDataset = false;
        // A fresh fetch owns the lock from here; clear the step-back deferral so this
        // run's finalize releases normally (the empty-page run that set it has ended).
        this.navigatingToPrevPage = false;
        this.hot?.instance?.updateSettings({readOnly: true}, false);
        return this.api.mapper.searchMany(query).pipe(
            tap((response: ListResponse<any>) => this.formatMapperJson(response.data)),
            concatMap((response: ListResponse<any>) =>
                this.fetchMissingSeries(response.data).pipe(
                    // Don't fail the page on a series-lookup failure: the rows still
                    // render, just with blank Series names until the next page change
                    // (or paste) repopulates the lookup. The paste and autocomplete
                    // paths already toast on their own lookup failures.
                    catchError(err => {
                        console.error('Failed to fetch missing series for page', err);
                        this.notification.openWarning(
                            'Some series names could not be loaded; change page or refresh to retry.'
                        );
                        return of(null);
                    }),
                    map(() => response)
                )
            ),
            catchError(err => {
                // An error escaping this endpoint would tear down PaginationDataSource's
                // $page stream (it runs inside a switchMap), killing the paginator for
                // the rest of the session — toast and complete empty instead, leaving
                // the previous page on the grid.
                console.error('Failed to load mappers page', err);
                this.notification.openWarning(
                    'Could not load the latest mappers. Please refresh to try again.'
                );
                return EMPTY;
            }),
            finalize(() => {
                // onPage stepped the paginator back off an empty page: the corrected
                // fetch is queued behind PaginationDataSource's debounce. Hold the
                // spinner + read-only lock across that window — otherwise the grid shows
                // the stale previous page yet accepts edits the corrected page's
                // loadData would silently discard. The corrected fetch clears the flag
                // at its start, so its own finalize performs the release.
                if (this.navigatingToPrevPage) {
                    return;
                }
                this.loading = false;
                // Restore editability unless a download owns the lock (its own finally
                // restores it once the export window closes).
                if (!this.isDestroyed && !this.downloadInProgress && this.hot?.instance) {
                    this.hot.instance.updateSettings({readOnly: false}, false);
                }
            })
        );
    };

    private formatMapperJson(rows: any[]): void {
        if (!rows) return;
        rows.forEach(mapper => {
            const value = mapper.attributes.json;
            if (value == null) return;
            // JSONB columns can arrive either as a JSON string (older list paths) or
            // already-parsed (object/array/scalar). HOT's text cell and the CSV/XLSX
            // export both render `[object Object]` for non-string objects, so coerce
            // to a string in both cases.
            if (typeof value === 'string') {
                try {
                    mapper.attributes.json = JSON.stringify(JSON.parse(value));
                } catch (e) {
                    // Leave invalid string JSON unchanged so we don't double-encode it,
                    // but log it — a non-parseable JSONB cell is a data-integrity signal.
                    console.warn('Mapper JSON not parseable; leaving raw', mapper.id, e);
                }
                return;
            }
            mapper.attributes.json = JSON.stringify(value);
        });
    }

    /**
     * Fetch the series_light records referenced by `rows` (defaults to every loaded
     * mapper) that we don't already hold, in batches small enough to keep the `in`
     * filter within URL limits, and merge them into the lookups. Used per mapper
     * page and before the full-dataset download/pivot projections.
     */
    fetchMissingSeries(rows?: any[], opts: { confirmRetryOnError?: boolean } = {}): Observable<any> {
        const source = rows ?? this.mappers;
        if (!source?.length) {
            return of(null);
        }
        const referencedIds = [...new Set(
            source.map(m => m?.relationships?.series?.data?.id).filter(Boolean)
        )];
        const known = new Set((this.series_light || []).map(s => s.id));
        const missing = referencedIds.filter(id => !known.has(id));
        if (!missing.length) {
            return of(null);
        }
        const requests = this.chunk(missing, CollectorMapperSheetViewComponent.PASTE_LOOKUP_BATCH_SIZE)
            .map(batch => {
                const opts = new SearchQueryOptions(batch.length, 1);
                opts.filters = [getBaseFilter(batch, 'id', 'in')];
                return this.api.series_light.searchMany(opts).pipe(first());
            });
        const lookup$ = forkJoin(requests).pipe(tap((responses: ListResponse<SeriesLight>[]) => {
            responses.forEach(result => {
                if (!result?.data) {
                    // A malformed/empty batch response would otherwise be skipped
                    // silently, leaving its series unresolved (blank cells) with no
                    // trace. Don't toast here (callers decide), but make it diagnosable.
                    console.warn('fetchMissingSeries: a batch returned no data array; some series may be unresolved');
                    return;
                }
                if (this.seriesLookups) {
                    this.mergeSeriesIntoLookups(result.data);
                } else {
                    // Pre-genTable path: seriesLookups doesn't exist yet, so mirror into
                    // series_light directly. mergeSeriesIntoLookups becomes the single
                    // update path once genTable has built the lookups.
                    const existing = new Set((this.series_light || []).map(s => s.id));
                    this.series_light = [...(this.series_light || []), ...result.data.filter(s => !existing.has(s.id))];
                }
            });
            // Cross-check: a referenced id we asked for but still don't hold means the
            // backend didn't return it (deleted/inaccessible) or a batch was dropped.
            // Surface the count so a partial resolution isn't completely silent.
            const known = new Set((this.series_light || []).map(s => s.id));
            const unresolved = missing.filter(id => !known.has(id)).length;
            if (unresolved > 0) {
                console.warn(`fetchMissingSeries: ${unresolved} referenced series could not be resolved`);
            }
        }));
        if (!opts.confirmRetryOnError) {
            return lookup$;
        }
        // Download/pivot opt in to a one-shot retry prompt on a TRANSIENT lookup failure
        // (network/timeout/5xx). A 4xx won't fix on retry; a series the backend simply
        // doesn't return isn't an error (HTTP 200 partial — handled above). On decline or
        // a repeat failure the error propagates so the caller can ship with blank names.
        return lookup$.pipe(
            catchError(err => {
                const status = err?.status;
                if (!(status === 0 || status >= 500)) {
                    return throwError(() => err);
                }
                return this.notification.openConfirm(
                    'Some series names could not be loaded. Retry?', 'Continue', 'Retry'
                ).afterDismissed().pipe(
                    map(result => result.dismissedByAction),
                    switchMap(retry => retry ? this.fetchMissingSeries(rows) : throwError(() => err)),
                );
            })
        );
    }

    private mergeSeriesIntoLookups(series: SeriesLight[]): string[] {
        const names: string[] = [];
        for (const s of series) {
            const display = this.getSeriesDisplay(s);
            if (!display) {
                continue;
            }
            if (!(display in this.seriesLookups.valueLookup)) {
                this.seriesLookups.source.push(display);
            }
            this.seriesLookups.valueLookup[display] = s.id;
            this.seriesLookups.idLookup[s.id] = display;
            this.seriesLookups.nameLookup[display] = s.attributes?.name?.trim() ?? '';
            names.push(display);
        }
        const existingIds = new Set((this.series_light || []).map(sl => sl.id));
        this.series_light = [...(this.series_light || []), ...series.filter(s => !existingIds.has(s.id))];
        return names;
    }

    /**
     * After a debounced Series search merges new lookups, repair any already-committed
     * rows whose typed text (_unverifiedSeriesText) now resolves to a just-fetched
     * series. The typed-edit path (seriesDataAccessor) commits before the async search
     * returns, so — unlike the paste path, which re-resolves its rows in finalisePaste —
     * it would otherwise leave a now-resolvable cell red. Mirror the paste finalisation:
     * set the series id, drop the unverified text, then re-validate only the Series
     * column (scoped so HOT's default date validator doesn't run over the ISO date
     * columns and flag them red).
     */
    private resolveUnverifiedSeriesRows(): void {
        // Skip while a paste is mid-flight: finalisePaste owns resolution and
        // re-validation for those rows and clears pasteInFlight when it's done.
        if (this.isDestroyed || this.pasteInFlight || !this.hot?.instance) {
            return;
        }
        const sourceData = this.hot.instance.getSourceData();
        let repaired = false;
        for (const row of sourceData) {
            const text = row?._unverifiedSeriesText;
            if (!text) {
                continue;
            }
            const id = this.findSeriesIdForText(text);
            if (!id) {
                continue;
            }
            if (!row.relationships) row.relationships = {};
            if (!row.relationships.series) row.relationships.series = {};
            row.relationships.series.data = {id, type: 'series'};
            delete row._unverifiedSeriesText;
            repaired = true;
        }
        if (!repaired) {
            return;
        }
        const seriesColIdx = (this.hot.instance.getColHeader() as string[]).indexOf('Series');
        if (seriesColIdx >= 0) {
            this.hot.instance.validateColumns([seriesColIdx], () => this.hot.instance.render());
        }
    }

    /**
     * Custom data accessor for the Series autocomplete column. Mirrors
     * HandsontableGeneratorService.genLookupDataSource but additionally preserves
     * the raw pasted/typed text on the row when the lookup misses, so an unresolved
     * name stays visible (and the validator paints it red) instead of going blank.
     */
    private seriesDataAccessor = (row: any, value?: any): any => {
        if (row == null) {
            return null;
        }
        if (row.relationships == null) row.relationships = {};
        if (!row.relationships.series) row.relationships.series = {};

        if (value === undefined) {
            const id = row.relationships.series.data?.id;
            if (id) {
                return this.seriesLookups.idLookup[id] ?? row._unverifiedSeriesText ?? null;
            }
            return row._unverifiedSeriesText ?? null;
        }

        if (!row.relationships.series.data) {
            row.relationships.series.data = {};
        }
        const text = value == null ? '' : String(value);
        this.clearStalePendingPaste(this.pendingSeriesPastes, row, text);
        const matchedId = this.findSeriesIdForText(text);
        row.relationships.series.data.id = matchedId;
        row.relationships.series.data.type = 'series';
        if (matchedId || text === '') {
            delete row._unverifiedSeriesText;
        } else {
            row._unverifiedSeriesText = text;
        }
        return value;
    };

    private getSeriesByName(pasted: any[][], config: PasteRange): void {
        const pasteRange = this.getEffectivePasteRange(pasted, config);
        if (!pasteRange) {
            this.pasteInFlight = false;
            return;
        }
        const headers = this.hot.instance.getColHeader() as string[];
        const seriesColIdx = headers.indexOf('Series');
        if (seriesColIdx < pasteRange.startCol || seriesColIdx > pasteRange.endCol) {
            this.pasteInFlight = false;
            return;
        }

        // Every value beforePaste whitelisted for this paste; released on every exit
        // below so a stranded entry can't keep an unresolved cell falsely valid.
        const whitelisted = this.pastedSeriesValues(pasted, pasteRange);

        // Capture the mapper row reference against HOT's live source data: HotInstance.query()
        // reloads HOT via loadData(deepCopy(source_data)) on every search/pagination, so
        // getSourceDataAtRow is the only handle that stays in sync regardless of sort/filter.
        const token = this.nextPasteLookupToken();
        const paste_entries: Array<{ rowData: any; text: string; token: number }> = [];
        const unknown = new Set<string>();
        for (let rowIdx = pasteRange.startRow; rowIdx <= pasteRange.endRow; rowIdx++) {
            const value = this.getPastedValueAt(pasted, pasteRange, rowIdx, seriesColIdx);
            if (value == null || value === '') {
                continue;
            }
            const text = String(value);
            const physicalRow = this.hot.instance.toPhysicalRow(rowIdx);
            if (physicalRow == null) continue;
            const rowData = this.hot.instance.getSourceDataAtRow(physicalRow);
            if (!rowData) continue;
            this.pendingSeriesPastes.set(rowData, {text, token});
            paste_entries.push({rowData, text, token});
            if (!this.findSeriesIdForText(text)) {
                this.getNameCandidates(text).forEach(candidate => unknown.add(candidate));
            }
        }

        if (paste_entries.length === 0) {
            whitelisted.forEach(v => this.pasteResolvingValues.delete(v));
            this.pasteInFlight = false;
            return;
        }

        const finalisePaste = (): void => {
            const notFound: string[] = [];
            paste_entries.forEach(({rowData, text, token}) => {
                if (!this.isCurrentPendingSeriesPaste(rowData, text, token)) {
                    return;
                }
                if (!rowData.relationships) rowData.relationships = {};
                if (!rowData.relationships.series) rowData.relationships.series = {};
                const id = this.findSeriesIdForText(text);
                rowData.relationships.series.data = {id, type: 'series'};
                if (id) {
                    delete rowData._unverifiedSeriesText;
                } else {
                    // Keep the pasted text on the row so the cell renders the unresolved
                    // name (in red, via the Series validator) rather than going blank.
                    rowData._unverifiedSeriesText = text;
                    notFound.push(text);
                }
                this.pendingSeriesPastes.delete(rowData);
            });
            // Release the in-flight pass so HOT re-validates against the now-merged
            // lookups (resolved → normal, genuinely missing → red). Release the full
            // whitelisted set (not just paste_entries) so values for rows that never
            // materialised aren't stranded — a stranded value keeps its cell falsely
            // valid for the rest of the session.
            whitelisted.forEach(v => this.pasteResolvingValues.delete(v));
            this.pasteInFlight = false;
            // Scope re-validation to the Series column so we don't run HOT's default
            // date validator over existing rows' ISO Changed/Created On values.
            //
            // Defer one macrotask: when finalisePaste runs synchronously from afterPaste
            // (sync path: unknown.size === 0 AND pasteRowsExist === true), HOT's async
            // validateChanges → applyChanges queue hasn't run yet, so the accessor
            // setter hasn't written the new value into the row. validateColumns
            // captures the cell value via getDataAtCell SYNCHRONOUSLY when it iterates,
            // so running it now would freeze the previous paste's stale getter snapshot
            // into the validator queue — leaving the cell red against text the setter
            // is about to overwrite. The setTimeout queues us after the
            // validateChanges/applyChanges chain so getDataAtCell sees the resolved
            // display string. The async-fetch path (unknown.size > 0) already runs
            // long after applyChanges so this defer is a no-op there.
            setTimeout(() => {
                if (this.isDestroyed || !this.hot?.instance) return;
                this.hot.instance.validateColumns([seriesColIdx], () => this.hot.instance.render());
            }, 0);
            if (notFound.length === 1) {
                this.notification.openError(`Series "${notFound[0]}" was not found`);
            } else if (notFound.length > 1) {
                this.notification.openError('Some series were not found');
            }
        };

        if (unknown.size === 0) {
            finalisePaste();
            return;
        }

        const requests = this.chunk(Array.from(unknown), CollectorMapperSheetViewComponent.PASTE_LOOKUP_BATCH_SIZE)
            .map(names => {
                const opts = new SearchQueryOptions(names.length, 1, 'name');
                opts.filters = [getBaseFilter(names, 'name', 'in')];
                return this.api.series_light.searchMany(opts).pipe(first());
            });

        forkJoin(requests).pipe(
            takeUntilDestroyed(this.destroyRef),
        ).subscribe({
            next: (responses: ListResponse<SeriesLight>[]) => {
                responses.forEach(response => this.mergeSeriesIntoLookups(response.data));
                finalisePaste();
            },
            error: () => {
                // Still finalize so rows get their {id: null} treatment and pasteInFlight
                // is cleared — leaving the flag stuck would freeze the validator session-wide.
                finalisePaste();
            },
        });
    }

    /**
     * Whether every row this paste targets already exists in HOT's source data. Used to
     * decide if afterPaste can resolve immediately or must defer to afterChange: a paste
     * that overflows the existing rows has its extra rows created by HOT's (async)
     * applyChanges, which runs after afterPaste, so toPhysicalRow/getSourceDataAtRow
     * return null for those rows until then.
     */
    private pasteRowsExist(pasted: any[][], cfg: PasteRange): boolean {
        const pasteRange = this.getEffectivePasteRange(pasted, cfg);
        if (!pasteRange) {
            return false;
        }
        const physical = this.hot.instance.toPhysicalRow(pasteRange.endRow);
        return physical != null && this.hot.instance.getSourceDataAtRow(physical) != null;
    }

    /**
     * The Series-column strings beforePaste whitelisted in `pasteResolvingValues`
     * for this paste, recomputed from the pasted block so every resolution path can
     * release them symmetrically (mirrors the add loop in beforePaste exactly). The
     * add covers the whole pasted block, so clearing only the rows that resolved
     * (paste_entries) would strand values for rows that never materialised and keep
     * their cells falsely valid for the rest of the session.
     */
    private pastedSeriesValues(pasted: any[][], pasteRange: PasteRange): string[] {
        const seriesColIdx = (this.hot.instance.getColHeader() as string[]).indexOf('Series');
        if (seriesColIdx < pasteRange.startCol || seriesColIdx > pasteRange.endCol) {
            return [];
        }
        const values: string[] = [];
        for (let rowIdx = pasteRange.startRow; rowIdx <= pasteRange.endRow; rowIdx++) {
            const value = this.getPastedValueAt(pasted, pasteRange, rowIdx, seriesColIdx);
            if (value != null && value !== '') {
                values.push(String(value));
            }
        }
        return values;
    }

    private getEffectivePasteRange(pasted: any[][], cfg: PasteRange): PasteRange | null {
        const clipboardRows = pasted?.length ?? 0;
        const clipboardCols = pasted?.[0]?.length ?? 0;
        if (!cfg || clipboardRows === 0 || clipboardCols === 0) {
            return null;
        }
        const startRow = Math.min(cfg.startRow, cfg.endRow);
        const startCol = Math.min(cfg.startCol, cfg.endCol);
        const selectedEndRow = Math.max(cfg.startRow, cfg.endRow);
        const selectedEndCol = Math.max(cfg.startCol, cfg.endCol);
        return {
            startRow,
            startCol,
            endRow: Math.max(selectedEndRow, startRow + clipboardRows - 1),
            endCol: Math.max(selectedEndCol, startCol + clipboardCols - 1),
        };
    }

    private getPastedValueAt(pasted: any[][], pasteRange: PasteRange, row: number, col: number): any {
        const sourceRow = (row - pasteRange.startRow) % pasted.length;
        const sourceCol = (col - pasteRange.startCol) % (pasted[0]?.length ?? 1);
        return pasted[sourceRow]?.[sourceCol];
    }

    setupSeriesPaste(): void {
        // Chain with any beforePaste/afterPaste the generator (or a future generic
        // hook on hot.settings) may have already registered: plain assignment
        // would silently drop them. Nothing in HandsontableGeneratorService writes
        // to these hooks today; this is defensive against a future change that
        // would otherwise break paste-by-name with no visible failure.
        const previousBeforePaste = this.hot.settings.beforePaste;
        const previousAfterPaste = this.hot.settings.afterPaste;
        this.hot.settings.beforePaste = (pasted: any[][], config: any[]) => {
            // Honour an earlier cancel decision. HOT treats only `false` as cancel,
            // so non-false (undefined / void) means "proceed".
            if (previousBeforePaste?.(pasted, config) === false) return false;
            const cfg = config?.[0];
            if (!cfg) return;
            const pasteRange = this.getEffectivePasteRange(pasted, cfg);
            if (!pasteRange) return;
            const headers = this.hot.instance.getColHeader() as string[];
            const seriesColIdx = headers.indexOf('Series');
            // Non-Series paste (Name/Collector/checkboxes/JSON) triggers no async Series
            // resolution, so the row cap doesn't apply and pasteInFlight must stay false.
            if (seriesColIdx < pasteRange.startCol || seriesColIdx > pasteRange.endCol) {
                return;
            }
            // Hard cap on Series-bearing pastes only: returning false cancels the paste
            // entirely — no rows written and afterPaste never fires, so no cleanup needed.
            const targetRows = pasteRange.endRow - pasteRange.startRow + 1;
            if (targetRows > CollectorMapperSheetViewComponent.PASTE_ROW_LIMIT) {
                this.notification.openError(
                    `Paste exceeds the ${CollectorMapperSheetViewComponent.PASTE_ROW_LIMIT}-row limit. ` +
                    `Please trim your selection and try again.`
                );
                return false;
            }
            this.pastedSeriesValues(pasted, pasteRange)
                .forEach(value => this.pasteResolvingValues.add(value));
            this.pasteInFlight = true;
        };
        this.hot.settings.afterPaste = (pasted, config) => {
            previousAfterPaste?.(pasted, config);
            const cfg = config?.[0];
            if (!cfg) {
                return;
            }
            const pasteRange = this.getEffectivePasteRange(pasted, cfg);
            const seriesColIdx = (this.hot.instance.getColHeader() as string[]).indexOf('Series');
            if (!pasteRange || seriesColIdx < pasteRange.startCol || seriesColIdx > pasteRange.endCol) {
                return;
            }
            // HOT validation is async, so a paste touching the validated Series column
            // creates its overflow rows after this hook. Resolve now only if every target
            // row already exists; otherwise defer to the CopyPaste.paste afterChange
            // (extraAfterChange), which fires once the rows are created.
            if (this.pasteRowsExist(pasted, cfg)) {
                this.getSeriesByName(pasted, cfg);
            } else {
                // A previous overflow paste may still be stashed if its afterChange
                // never fired; release its whitelisted values before replacing it so
                // they can't leak into pasteResolvingValues permanently.
                if (this.pendingPasteContext) {
                    const pendingRange = this.getEffectivePasteRange(
                        this.pendingPasteContext.pasted,
                        this.pendingPasteContext.config,
                    );
                    if (pendingRange) {
                        this.pastedSeriesValues(this.pendingPasteContext.pasted, pendingRange)
                            .forEach(v => this.pasteResolvingValues.delete(v));
                    }
                }
                this.pendingPasteContext = {pasted, config: cfg};
            }
        };
    }

    /**
     * Guard the context-menu delete. Deleting an EXISTING (id-bearing) row runs a server
     * delete + scheduleMapperReload re-fetch that discards other rows' pending edits (and
     * races fetchAllPages mid-download), so it stays blocked while dirty or downloading. An
     * id-less NEW row is removed locally (no server call, no reload — beforeRemoveRow is gated
     * on removedIds), so it stays enabled as the in-app discard for an incomplete new row that
     * would otherwise deadlock the sheet. Callbacks re-evaluate on every menu open (HOT reads
     * contextMenu at construction), reflecting the live selection and dirty/download state.
     */
    private setupDeleteGuard(): void {
        const deleteItem = this.hot.settings?.contextMenu?.items?.delete_row;
        if (!deleteItem) {
            return;
        }
        deleteItem.disabled = () => {
            if (this.downloadInProgress) {
                return true;
            }
            return this._hasUnsavedChanges() && this.selectionHasExistingRow();
        };
        deleteItem.name = () => {
            if (this.downloadInProgress) {
                return 'Delete Row Permanently — wait for the download to finish';
            }
            if (!this.selectionHasExistingRow()) {
                return 'Discard New Row';
            }
            if (this._hasUnsavedChanges()) {
                return 'Delete Row Permanently — save or discard changes first';
            }
            return 'Delete Row Permanently';
        };
    }

    /**
     * True when the grid selection includes at least one saved (id-bearing) row.
     * No selection → conservative true (keep the guard). See setupDeleteGuard.
     */
    private selectionHasExistingRow(): boolean {
        const ranges = this.hot.instance?.getSelected();
        if (!ranges?.length) {
            return true;
        }
        for (const [startRow, , endRow] of ranges) {
            const from = Math.min(startRow, endRow);
            const to = Math.max(startRow, endRow);
            for (let row = from; row <= to; row++) {
                if (this.hasCellValue(this.hot.instance.getDataAtRowProp(row, 'id'))) {
                    return true;
                }
            }
        }
        return false;
    }

    private findSeriesIdForText(text: string): string | null {
        const trimmed = text.trim();
        const exactDisplayId = this.seriesLookups.valueLookup[text] ?? this.seriesLookups.valueLookup[trimmed];
        if (exactDisplayId) {
            return exactDisplayId;
        }
        const byName = (this.series_light || []).find(s => s?.attributes?.name?.trim() === trimmed);
        return byName?.id ?? null;
    }

    private getCachedSeriesMatches(query: string): string[] {
        const lower = query.toLowerCase();
        return query
            ? this.seriesLookups.source.filter(s => s && s.toLowerCase().includes(lower))
            : this.seriesLookups.source;
    }

    private getSeriesDisplay(series: SeriesLight): string {
        const description = series.attributes?.description?.trim();
        const name = series.attributes?.name?.trim() ?? '';
        return description ? `${name}-${description}` : name;
    }

    private getNameCandidates(text: string): string[] {
        const trimmed = text.trim();
        if (!trimmed) {
            return [];
        }
        const candidates = new Set<string>([trimmed]);
        const parts = trimmed.split('-');
        for (let i = 1; i < parts.length; i++) {
            const candidate = parts.slice(0, i).join('-').trim();
            if (candidate) {
                candidates.add(candidate);
            }
        }
        return Array.from(candidates);
    }

    private uniqueNames(names: string[]): string[] {
        return Array.from(new Set(names.filter(name => name)));
    }

    private nextPasteLookupToken(): number {
        this.pasteLookupToken += 1;
        return this.pasteLookupToken;
    }

    private clearStalePendingPaste(pendingPastes: WeakMap<any, PendingPasteLookup>, row: any, text: string): void {
        if (!row) {
            return;
        }
        const pending = pendingPastes.get(row);
        if (pending && pending.text !== text) {
            pendingPastes.delete(row);
        }
    }

    private isCurrentPendingPaste(
        pendingPastes: WeakMap<any, PendingPasteLookup>,
        rowData: any,
        text: string,
        token: number,
    ): boolean {
        const pending = pendingPastes.get(rowData);
        return pending?.token === token
            && pending.text === text
            && Boolean(this.hot.instance?.getSourceData().includes(rowData));
    }

    private isCurrentPendingSeriesPaste(rowData: any, text: string, token: number): boolean {
        if (!this.isCurrentPendingPaste(this.pendingSeriesPastes, rowData, text, token)) {
            return false;
        }
        if (rowData?._unverifiedSeriesText) {
            return rowData._unverifiedSeriesText === text;
        }
        const currentId = rowData?.relationships?.series?.data?.id;
        return Boolean(currentId) && currentId === this.findSeriesIdForText(text);
    }

    private chunk<T>(items: T[], size: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < items.length; i += size) {
            chunks.push(items.slice(i, i + size));
        }
        return chunks;
    }

    genTable() {
        // seriesLookups starts from the series referenced by the first page (resolved
        // in pageMappers via fetchMissingSeries) and grows incrementally as the user
        // changes page, searches, or pastes — never a full-catalogue preload.
        this.seriesLookups = this.handsontableGenerator.gen_lookups(this.series_light);
        let collectorLookups = this.handsontableGenerator.gen_lookups(this.collectors);

        this.schema = {
            id: null,
            type: 'mapper',
            attributes: {
                source_name: null,
                pre_process: null,
                send_alerts: null,
                save_opsdata: null,
                create_events: null,
                latest_date: null,
                invert_signal: null,
                priority: null,
                interpolated: null,
                aggregation_method: null,
                high_frequency_collection: null,
                json: null
            }, relationships: {
                series: {data: {type: 'series', id: null}},
                collector: {data: {type: 'collector', id: null}}
            }
        };

        this.column_list = [{
            data: 'attributes.source_name',
            title: 'Name'
        }, {
            data: this.seriesDataAccessor,
            title: 'Series',
            type: 'autocomplete',
            trimDropdown: false,
            allowInvalid: true,
            source: (query: string, process: (names: string[]) => void) => {
                const q = (query ?? '').trim();
                if (q && this.seriesLookups.valueLookup[q] !== undefined) {
                    process([q]);
                    return;
                }
                // Under 3 chars, serve cached display strings rather than hitting the
                // server — short ilike '%q%' searches against name+description are
                // expensive. Three chars makes the contains query usefully selective;
                // shorter typing still gets local cache results immediately.
                if (q.length < 3) {
                    const cached = this.getCachedSeriesMatches(q).slice(0, 100);
                    if (!q) {
                        process(cached);
                        return;
                    }
                    this.seriesQuery$.next({query: q, process, exactNameOnly: true, cachedNames: cached});
                    return;
                }
                this.seriesQuery$.next({query: q, process});
            },
            validator: (value, callback) => {
                if (value == null || value === '') {
                    callback(true);
                    return;
                }
                // While a paste is mid-flight, treat the pasted text as valid so the cell
                // doesn't flash red between the local lookup miss and the server resolve.
                if (this.pasteResolvingValues.has(value)) {
                    callback(true);
                    return;
                }
                callback(this.findSeriesIdForText(String(value)) !== null);
            },
        }, {
            data: this.handsontableGenerator.genLookupDataSource(collectorLookups, 'collector'),
            title: 'Collector',
            type: 'autocomplete',
            trimDropdown: false,
            strict: true,
            source: collectorLookups.source,
            allowInvalid: true
        }, {
            data: 'attributes.pre_process',
            title: 'PreProcess'
        }, {
            data: 'attributes.latest_date',
            title: 'Latest Date',
            readOnly: true,
            type: 'date',
            renderer: 'date_formatter'
        }, {
            data: 'attributes.invert_signal',
            title: 'Invert Signal',
            type: 'checkbox'
        }, {
            data: 'attributes.save_opsdata',
            title: 'Save Operational Data',
            type: 'checkbox'
        }, {
            data: 'attributes.create_events',
            title: 'Create Events',
            type: 'checkbox'
        }, {
            data: 'attributes.send_alerts',
            title: 'Invert Signal',
            type: 'checkbox'
        }, {
            data: 'attributes.priority',
            title: 'Priority'
        }, {
            data: 'attributes.interpolated',
            title: 'Interpolate ',
            type: 'checkbox'
        }, {
            data: 'attributes.aggregation_method',
            title: 'Historian aggregation method ',
            type: 'dropdown',
            source: this.mapper_aggregation_methods,
        }, {
            data: 'attributes.high_frequency_collection',
            title: 'High Frequency Collection',
            type: 'checkbox'
        }, {
            data: 'attributes.json',
            title: 'JSON',
            type: 'text',
            wordWrap: true,
            width: 300
        }, {
            data: 'attributes.created_by_name',
            readOnly: true,
            title: 'Created By'
        }, {
            data: 'attributes.changed_by_name',
            readOnly: true,
            title: 'Changed By'
        }, {
            data: 'attributes.changed_on',
            readOnly: true,
            title: 'Changed On',
            type: 'date',
            renderer: 'date_formatter'
        }, {
            data: 'attributes.created_on',
            readOnly: true,
            title: 'Created On',
            type: 'date',
            renderer: 'date_formatter'
        }];

        // A paste's overflow rows are only created when HOT applies the change
        // (applyChanges, which fires this afterChange) — and because Series validation
        // is async that can happen after afterPaste. If afterPaste deferred resolution
        // because the rows didn't exist yet, run it now that they do.
        const extraAfterChange = (_changes: [number, string | number, any, any][], source: string) => {
            if (source === 'CopyPaste.paste' && this.pendingPasteContext) {
                const {pasted, config} = this.pendingPasteContext;
                this.pendingPasteContext = null;
                this.getSeriesByName(pasted, config);
            }
        };

        // No `paginate` arg: paging is driven by the mat-paginator + PaginationDataSource
        // (createDataSource), not the generator's scroll-append hook.
        this.hot = this.handsontableGenerator.generateTable(
            this.api.mapper, this.schema, this.column_list, this.hot, extraAfterChange);
        // setupSeriesPaste assigns beforePaste/afterPaste onto hot.settings; HOT
        // reads hooks from settings at construction time, so this MUST run before
        // initializeInstance() below. Reordering after the constructor would
        // silently disable paste-by-name (the hooks would never bind).
        this.setupSeriesPaste();
        this.setupDeleteGuard();
        this.hot.ready = true;
        // Seed the grid with a copy so the first page's edits don't mutate this.mappers
        // (the server snapshot); later pages do the same in onPage.
        this.hot.settings.data = utils.deepCopy(this.mappers);
        this.hot.settings.rowHeaders = false;
        // New rows are added via the toolbar's Add Row button (addSpareRow), not
        // trailing spare rows — the generator's default of 3 kept the empty rows
        // out of reach behind scroll-pagination (the original WIRE-14962 complaint)
        // and would inflate every page by three blanks here.
        this.hot.settings.minSpareRows = 0;
        this.hot.settings.beforeRemoveRow = (index: number, amount: number, physicalRows?: number[]) => {
            const sourceData = this.hot.instance.getSourceData();
            const removedRows = physicalRows?.length ? physicalRows : Array.from({length: amount}, (_row, offset) => index + offset);
            // new_rows holds SOURCE row indexes, so a removal must drop the removed index
            // and shift the survivors above it down — otherwise a discarded new row's index
            // lingers (sheet stays dirty) or points at the wrong row. (change_ids is keyed
            // by id, pruned separately below.)
            if (this.hot.new_rows?.length) {
                const removedSet = new Set(removedRows);
                this.hot.new_rows = this.hot.new_rows
                    .filter(row => !removedSet.has(row))
                    .map(row => row - removedRows.filter(removed => removed < row).length);
            }
            const removedIds = removedRows.map(row => sourceData[row]?.id).filter(Boolean);
            if (removedIds.length) {
                // Keep the visible counters honest in the brief window before the
                // deferred reload refreshes them: totalRowCount feeds the grand-total
                // label, totalCount the paginator's range label (via paginator.length).
                if (this.totalRowCount != null) {
                    this.totalRowCount = Math.max(0, this.totalRowCount - removedIds.length);
                }
                if (this.totalCount != null) {
                    this.totalCount = Math.max(0, this.totalCount - removedIds.length);
                    this.paginator.length = this.totalCount;
                }
                // Drop the deleted rows from the pending-edit set first: a deleted row
                // can't be patched (a lingering id would error on the next save), and
                // leaving it in change_ids would make the post-delete reload warn about
                // "unsaved changes" that were really just the now-gone row.
                if (this.hot.change_ids?.length) {
                    this.hot.change_ids = this.hot.change_ids.filter(id => !removedIds.includes(id));
                }
                // A permanent delete commits a server-side removal that shifts every
                // backend offset after the deleted row, so the current page's contents
                // are stale (the row that crossed the page boundary would be missing).
                // Re-fetch the current page so it realigns with server truth.
                this.scheduleMapperReload();
            }
        };
        // Construct HOT via initializeInstance (not `new Handsontable` directly)
        // so source_data is seeded from settings.data. setupSeriesPaste() above
        // already wrote beforePaste/afterPaste onto hot.settings, and HOT reads
        // hooks at construction time, so paste-by-name remains wired.
        this.hot.initializeInstance(this.hot_anchor.nativeElement);
    }

    /**
     * Toolbar "Add row": appends one empty row at the end of the grid (spare rows
     * are disabled — see minSpareRows above). setup_save only persists it once the
     * required Name is filled, so an untouched added row is dropped on reload.
     */
    addSpareRow(): void {
        this.hot.instance?.alter('insert_row', this.hot.instance.getData().length, 1);
    }

    save() {
        // Safe no-op if the header Save is clicked before the first page builds the
        // table (the header is registered synchronously in ngOnInit, so it can be
        // clicked while the initial load is still in flight).
        if (!this.firstPageReady || !this.hot?.instance) {
            return;
        }
        // Don't save into a grid whose rows are mid-replacement (page change /
        // account filter / search reload): hot.save() reads getSourceData(), which
        // onPage()'s loadData() is about to swap when the in-flight page lands.
        if (this.loading) {
            this.notification.openError('Cannot save while mappers are loading. Please try again.');
            return;
        }
        // A series-less mapper does no useful work — the ingestion path skips any mapper
        // whose series is null (backend/resources/raw_data.py). Guard only the rows that
        // would NEWLY introduce one: a row carrying unresolved typed/pasted Series text
        // (_unverifiedSeriesText), or a NEW mapper (with the required Name) that has no
        // series. Pre-existing persisted mappers with a null series are intentionally NOT
        // blocked — they predate this rule (Mapper.series_id is still nullable, with a
        // backend TODO to migrate them) and blocking them would stop unrelated edits on the
        // sheet from saving. Re-validate the Series column and name the offending rows.
        const invalidRows = this.getRowsWithMissingSeries(this.hot.instance.getSourceData());
        if (invalidRows.length) {
            const rows = invalidRows.map(row => row + 1).join(', ');
            this.notification.openError(`Cannot save. Series missing or unresolved at row(s): ${rows}`);
            const seriesColIdx = (this.hot.instance.getColHeader() as string[]).indexOf('Series');
            if (seriesColIdx >= 0) {
                this.hot.instance.validateColumns([seriesColIdx], () => this.hot.instance.render());
            }
            return;
        }
        const results = this.hot.save();
        Promise.allSettled(results.deferreds).then(() => {
            this.mappers = this.hot.instance.getSourceData();
            // Deliberately no dataSource.refresh() here: a refresh would re-sort
            // just-created rows to their server positions (likely another page),
            // yanking them out from under the user. totalCount/paginator.length stay
            // briefly stale after saving new rows, until the next page event.
            this.refreshTotalRowCount();
        });
    }

    /**
     * Visual row numbers of rows whose save would NEWLY introduce a series-less mapper:
     * a row with unresolved typed/pasted text (_unverifiedSeriesText), or a NEW row (no
     * id) carrying the required Name but no series id. A series-less mapper does no useful
     * work (ingestion skips it), so we stop new ones being created — but an EXISTING
     * persisted mapper with a null series is deliberately allowed through (those predate
     * this rule; Mapper.series_id is still nullable, with a backend TODO to migrate them),
     * so a sheet that merely contains one can still save unrelated edits. Blank/partial
     * rows setup_save won't persist (no id and no Name) are skipped. Returned in
     * visual-row order so the error names the row the user sees.
     */
    private getRowsWithMissingSeries(sourceData: any[]): number[] {
        const invalidRows: number[] = [];
        sourceData.forEach((row, index) => {
            if (!this.hasMapperRowData(row)) {
                return;
            }
            const hasUnverifiedText = Boolean(row?._unverifiedSeriesText);
            const isNewRow = !this.hasCellValue(row?.id);
            const hasSeries = Boolean(row?.relationships?.series?.data?.id);
            if (hasUnverifiedText || (isNewRow && !hasSeries)) {
                const visualRow = this.hot.instance.toVisualRow(index);
                invalidRows.push(visualRow != null && visualRow >= 0 ? visualRow : index);
            }
        });
        return invalidRows;
    }

    /**
     * Whether a row will be persisted as a mapper: an existing record (id) or a new row
     * carrying the required Name (source_name) — the exact set setup_save saves
     * (HotInstance required_keys = ['attributes.source_name']). Gating the
     * missing-series check on this skips blank spare rows and keeps the error aimed at
     * rows that would otherwise be saved series-less.
     */
    private hasMapperRowData(row: any): boolean {
        return this.hasCellValue(row?.id) || this.hasCellValue(row?.attributes?.source_name);
    }

    private hasCellValue(value: any): boolean {
        return value !== null && value !== undefined && value !== '';
    }

    /**
     * Grand total of mappers across every account the user can access. Uses an
     * unfiltered page_size=1 query purely to read meta.count — backend mapper reads
     * are scoped to accessible accounts (not the active account), so this is the
     * true total regardless of the table's account/search filter.
     */
    private refreshTotalRowCount() {
        this.api.mapper.searchMany(new SearchQueryOptions(1)).pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: response => {
                this.totalRowCount = response.meta?.count ?? 0;
            },
            // The grand-total label is non-critical, so don't toast — but don't swallow
            // silently either: log so a failed count is diagnosable (otherwise the
            // total just freezes at its previous value with no signal).
            error: err => console.error('Failed to refresh mapper total count', err),
        });
    }

    /**
     * Re-fetch the current page after a persisted delete so the grid realigns with
     * the server's row offsets (the delete shifts every row after it). Deferred and
     * coalesced via a flag so a multi-row delete — which fires beforeRemoveRow once
     * per removed row synchronously — triggers a single reload. A delete that
     * empties the last page is handled by onPage's page-back step.
     */
    private scheduleMapperReload(): void {
        if (this.mapperReloadScheduled) {
            return;
        }
        this.mapperReloadScheduled = true;
        setTimeout(() => {
            this.mapperReloadScheduled = false;
            if (this.isDestroyed || !this.hot?.instance) {
                return;
            }
            // The reload replaces source_data via onPage()'s loadData(), discarding
            // any pending edits — the reason the search/account-filter wrappers guard
            // with guardUnsavedChanges first. A permanent delete can't be blocked, so
            // surface the discard. (The now-stale edit tracking is cleared by onPage
            // on the reload's success path, so a failed reload doesn't strand
            // untracked edits.)
            if (this._hasUnsavedChanges()) {
                this.notification.openWarning(
                    'Unsaved changes were discarded when the table refreshed after the delete.'
                );
            }
            this.markPageLoadPending();
            this.dataSource?.refresh();
        }, 0);
    }

    /**
     * Server-side filter the table by the selected accounts (multi-select). An empty
     * selection clears the account filter (all accessible mappers). The grand-total
     * label (totalRowCount) is intentionally left unchanged.
     */
    onAccountsSelected(accounts: Account[]) {
        if (this.guardUnsavedChanges('filtering by account')) {
            return;
        }
        this.accountFilterIds = (accounts ?? []).map(account => account.id);
        this.applyFilters();
    }

    // Guarded wrapper: blocks the server-side reload while edits are pending
    // (see guardUnsavedChanges).
    onSearchSubmitted(value: string): void {
        if (this.guardUnsavedChanges('searching')) {
            return;
        }
        this.search = value;
        this.applyFilters();
    }

    // Guarded wrapper: blocks the server-side reload while edits are pending
    // (see guardUnsavedChanges).
    onSearchCleared(): void {
        if (this.guardUnsavedChanges('clearing the search')) {
            return;
        }
        this.search = '';
        this.applyFilters();
    }

    /**
     * Shared guard for the search / clear / account-filter wrappers: blocks a
     * server-side reload while the sheet has unsaved edits. Without it, the
     * loadData() the next page emission performs in onPage() would silently
     * overwrite pending change_ids/new_rows. Returns true (and toasts) when a
     * reload must be blocked.
     */
    private guardUnsavedChanges(action: string): boolean {
        if (!this._hasUnsavedChanges()) {
            return false;
        }
        this.notification.openError(
            `You have unsaved changes. Save or discard your changes before ${action}.`
        );
        return true;
    }

    /**
     * The search box's server-side filter. Reproduces the or-filter shape the old
     * HotInstance.queryFilter built from query_lookup — attributes as ilike,
     * relationships via `has` (or-of-fields when a relationship searches more than
     * one column) — inlined now that the component owns its queries.
     */
    private buildSearchFilter(search: string): object {
        const val = `%${search}%`;
        return {
            or: [
                ...['source_name', 'pre_process', 'priority', 'aggregation_method']
                    .map(attr => ({name: attr, op: 'ilike', val})),
                {
                    name: 'series', op: 'has', val: {
                        or: [
                            {name: 'name', op: 'ilike', val},
                            {name: 'description', op: 'ilike', val},
                        ]
                    }
                },
                {name: 'collector', op: 'has', val: {name: 'name', op: 'ilike', val}},
                {name: 'created_by', op: 'has', val: {name: 'name', op: 'ilike', val}},
                {name: 'changed_by', op: 'has', val: {name: 'name', op: 'ilike', val}},
            ]
        };
    }

    /**
     * Compose the table's full server-side filter set: the multi-account selection
     * (when set) plus the active search. Pushed into PaginationDataSource on every
     * filter change, and read by the bulk-download path so exports match the table.
     */
    private buildCurrentFilters(): object[] {
        const filters: object[] = [];
        if (this.accountFilterIds?.length) {
            filters.push(getRelationWithManyIdsFilter('account', this.accountFilterIds));
        }
        if (this.search) {
            filters.push(this.buildSearchFilter(this.search));
        }
        return filters;
    }

    // All filter changes funnel through here: jump back to the first page —
    // filterBy resets the query's page_number but NOT the paginator widget's
    // pageIndex, so without the explicit reset the range label would disagree
    // with the page actually fetched — then push the recomposed filters.
    private applyFilters(): void {
        this.markPageLoadPending();
        this.paginator.pageIndex = 0;
        this.dataSource.filterBy(this.buildCurrentFilters());
    }

    private markPageLoadPending(): void {
        this.currentPageIsCompleteDataset = false;
        this.loading = true;
        this.hot?.instance?.updateSettings?.({readOnly: true}, false);
    }

    /**
     * Header "Download" button. Lets the user pick Excel or CSV (mirrors
     * series-sheet-view), then runs executeDownload(). No-op — and no menu shown —
     * if a download is already running, the first page hasn't returned yet, a page
     * fetch is still in flight, or there are unsaved edits (we don't want the bulk
     * loadData() to wipe in-progress edits).
     *
     * The `loading` guard is also load-bearing: opening the picker mid page-fetch
     * would leave that fetch in flight while downloadInProgress is set, and if it
     * then failed its readOnly lock would never be released (pageMappers' finalize
     * skips the restore during a download and the failure path sets no
     * pendingPageRefresh to replay it). Refusing to open while loading removes that.
     */
    download(): void {
        if (!this.firstPageReady || this.downloadInProgress || this.loading) return;

        if (this._hasUnsavedChanges()) {
            this.notification.openError(
                'You have unsaved changes. Save or discard your changes before downloading.'
            );
            return;
        }

        // Position the format picker under the header's page-menu trigger. The Download
        // item lives inside that menu (only in the DOM while open), and header buttons
        // don't render their `class` (so `.icon-download` never exists) — the trigger is
        // the stable anchor. Scoped to `.page-title` so it can't match elsewhere.
        const trigger = document.querySelector('.page-title .icon-menu') ?? document.querySelector('.icon-menu');
        const rect = trigger?.getBoundingClientRect();
        const handle = this.popupService.open(SheetDownloadFormatMenuComponent, {
            x: (rect?.left ?? 0) - 60,
            y: (rect?.bottom ?? 0) + 40,
            inputs: {fileOptions: SHEET_DOWNLOAD_OPTIONS},
        });
        this.downloadPopupHandle = handle;
        // Claim the guard SYNCHRONOUSLY, before the user picks a format. Previously
        // downloadInProgress was only set inside executeDownload() (after the pick), so a
        // second click of the Download header button while the picker was still open passed
        // the top-of-method check and opened a second picker — and selecting a format in
        // each ran two concurrent bulk fetches racing over this.mappers / totalCount.
        // executeDownload() clears it again on every exit path.
        this.downloadInProgress = true;

        let formatChosen = false;
        handle.componentRef.instance.handleDownload
            // take(1): one selection ends the subscription. Without it, opening and
            // dismissing the picker repeatedly leaked a live subscription per cycle.
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe((fileType: string) => {
                formatChosen = true;
                handle.close();
                this.executeDownload(fileType);
            });
        // The picker emits handleDownload only on an explicit pick; dismissing it
        // (backdrop / Escape) just disposes the overlay. Release the guard on that teardown
        // when no format was chosen, otherwise it stays true forever and blocks every later
        // download (and the search/account controls bound to it).
        handle.overlayRef.detachments()
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                if (!formatChosen) {
                    this.downloadInProgress = false;
                    // A page emission that landed while the picker was open was
                    // skipped by onPage (downloadInProgress) — apply it now.
                    if (this.pendingPageRefresh) {
                        this.pendingPageRefresh = false;
                        this.markPageLoadPending();
                        this.dataSource?.refresh();
                    }
                }
            });
    }

    private takeDownloadPageSnapshot(): DownloadPageSnapshot {
        return {mappers: utils.deepCopy(this.mappers ?? [])};
    }

    private restoreDownloadPageSnapshot(snapshot: DownloadPageSnapshot): boolean {
        if (!this.hot?.instance) {
            return false;
        }
        try {
            this.mappers = utils.deepCopy(snapshot.mappers ?? []);
            const pageData = utils.deepCopy(this.mappers);
            this.hot.instance.loadData(pageData);
            this.hot.source_data = pageData;
            this.hot.change_ids = [];
            this.hot.new_rows = [];
            this.currentPageIsCompleteDataset =
                this.paginator.pageIndex === 0 && this.mappers.length < this.paginator.pageSize;
            return true;
        } catch (err) {
            console.error('Failed to restore mapper page after download', err);
            return false;
        }
    }

    /**
     * Fetch every remaining mapper for the *current* account/search filter
     * (buildCurrentFilters()), then export in the chosen format. While the bulk
     * fetch runs the grid is set to readOnly so keystrokes aren't silently
     * overwritten by the loadData() that swaps in the freshly fetched, edit-free
     * mapper list.
     */
    private async executeDownload(fileType: string): Promise<void> {
        const baseName = `collector_mappers_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}`;

        // Already complete for this filter — just export what's in the table. This
        // deliberately does NOT use meta.count/totalCount: counts can be stale or
        // under-reported. A short first page is the only trusted completion signal.
        if (!this.loading && this.currentPageIsCompleteDataset) {
            // exportRows runs OUTSIDE the main try/catch below, and executeDownload is
            // invoked from download()'s subscribe (un-awaited), so a CSV failure here
            // would be an unhandled rejection with no user feedback. Guard it (the xlsx
            // branch already has its own .catch inside exportRows).
            let seriesComplete = true;
            try {
                // The single loaded page had its series resolved at page load (pageMappers),
                // but a transient lookup failure there is tolerated — the rows still render,
                // with the unresolved ids absent from seriesLookups. Re-resolve before the
                // export so the Series column isn't written blank, matching the full-fetch
                // path: retry once on a transient failure, then ship what resolved (the
                // accessor falls back to blank) rather than aborting on a names miss.
                try {
                    await firstValueFrom(this.fetchMissingSeries(this.mappers, {confirmRetryOnError: true}));
                } catch (err) {
                    console.warn('Series names could not be fully resolved for the download', err);
                    seriesComplete = false;
                }
                if (this.isDestroyed) return;
                await this.exportRows(fileType, baseName);
                if (!seriesComplete) {
                    this.notification.openWarning('Download complete, but some series names could not be loaded.');
                }
            } catch (err) {
                console.error('Failed to export mappers', err);
                this.notification.openError('Download failed. Please try again.');
            } finally {
                // download() set the guard synchronously; this fast path doesn't enter the
                // full-fetch block below (which clears it in its own finally), so release
                // it here too — and apply any page emission onPage skipped meanwhile.
                this.downloadInProgress = false;
                if (this.pendingPageRefresh) {
                    this.pendingPageRefresh = false;
                    this.markPageLoadPending();
                    this.dataSource?.refresh();
                }
            }
            return;
        }

        const pageSnapshot = this.takeDownloadPageSnapshot();
        let fullDatasetInstalled = false;
        // A page emission landing during the window below is skipped by onPage
        // (the downloadInProgress guard — claimed synchronously back in download(),
        // not re-established here) and replayed by the finally's refresh, so an
        // in-flight page fetch can't write to this.mappers / this.totalCount while
        // the download loop is running.
        // Lock the grid for the full bulk-fetch + export window. The template only
        // disables the search/account/insert controls and the paginator — without
        // this readOnly toggle, a user could edit cells while we paginate
        // MAX_PAGE_SIZE chunks, and the loadData() call below would silently
        // overwrite those edits when it swaps in the freshly fetched mappers list.
        this.hot.instance?.updateSettings({readOnly: true}, false);
        const filters = this.buildCurrentFilters();

        const totalForMessage = this.totalCount ?? '?';
        // Pass duration=0 so the toast stays visible for the entire (potentially
        // multi-page) download instead of auto-dismissing after the default timeout.
        const progressNotif = this.notification.openInfo(
            `Loading full dataset (${totalForMessage} rows) for download…`, 0
        );

        try {
            const {rows, hitCap} = await fetchAllPages<MapperRow>(this.api.mapper, {
                filters,
                // Match the table's Name order (pageMappers) so the exported file
                // isn't silently re-sorted by id when installFullDataset swaps the grid.
                sort: CollectorMapperSheetViewComponent.MAPPER_SORT,
                isCancelled: () => this.isDestroyed,
            });
            if (this.isDestroyed) return;
            if (hitCap) {
                this.notification.openError(
                    'Download aborted: exceeded maximum page count. Please contact support if this persists.'
                );
                return;
            }
            // We're past the hitCap early-return, so `rows` is the COMPLETE deduped set for
            // the current filter — set totalCount from it unconditionally. A narrowing
            // account/search filter applied just before Download yields fewer rows than the
            // old count, and the previous `>`-only update left that stale larger value in
            // the range label, the next download's progress text, and the fast-path
            // comparison.
            this.totalCount = rows.length;
            this.paginator.length = this.totalCount;
            this.formatMapperJson(rows);
            // Resolve the series these rows reference so the Series column renders names
            // in the export — both the CSV (toCSVString) and Excel (exportHandsontableToXlsx)
            // read the column through its accessor, which needs the ids merged into
            // seriesLookups. A transient lookup failure prompts a one-shot retry; we never
            // abort the export for a names miss — ship what resolved and flag the gap after.
            let seriesComplete = true;
            try {
                await firstValueFrom(this.fetchMissingSeries(rows, {confirmRetryOnError: true}));
            } catch (err) {
                console.warn('Series names could not be fully resolved for the download', err);
                seriesComplete = false;
            }
            if (this.isDestroyed) return;
            // Load the complete dataset into the grid for the export window — the
            // exporters read the live instance (installFullDataset is just
            // deepCopy + loadData here, keeping source_data aligned with the grid).
            // The finally below restores the current page afterwards.
            this.mappers = rows;
            this.hot.installFullDataset(this.mappers);
            fullDatasetInstalled = true;
            await this.exportRows(fileType, baseName);
            if (!seriesComplete) {
                this.notification.openWarning('Download complete, but some series names could not be loaded.');
            }
        } catch (err) {
            if (this.isDestroyed) return;
            console.error('Failed to fetch full mapper dataset for download', err);
            this.notification.openError('Failed to load full dataset for download. Please try again.');
        } finally {
            progressNotif?.dismiss();
            if (!this.isDestroyed && fullDatasetInstalled && !this.restoreDownloadPageSnapshot(pageSnapshot)) {
                this.notification.openError('Download finished, but the table could not be restored. Please refresh.');
                return;
            }
            if (!this.isDestroyed) {
                // The grid has already been restored before the download guard and
                // read-only lock are released, so the user never edits the temporary
                // export dataset.
                this.pendingPageRefresh = false;
                this.downloadInProgress = false;
                this.hot?.instance?.updateSettings?.({readOnly: false}, false);
            }
        }
    }

    /**
     * Export the rows currently loaded in the grid in the chosen format. Both paths
     * read the live Handsontable instance (resolved accessor values, incl. Series /
     * Collector lookups), so the full filtered dataset loaded by executeDownload() is
     * what gets written.
     */
    private async exportRows(fileType: string, baseName: string): Promise<void> {
        if (fileType === 'xlsx') {
            await exportHandsontableToXlsx(this.hot.instance, `${baseName}.xlsx`)
                .catch(err => this.notification.openError('Download failed: ' + (err?.message || 'An unexpected error occurred')));
        } else {
            this.hot.download(`${baseName}.csv`);
        }
    }

    private _hasUnsavedChanges(): boolean {
        const changes = this.hot?.change_ids?.length ?? 0;
        const newRows = this.hot?.new_rows?.length ?? 0;
        return changes > 0 || newRows > 0;
    }

    /**
     * Template-facing dirty flag. Disables <account-filter> and the paginator while
     * the sheet has unsaved edits: changing the account otherwise lets mat-select
     * show the newly-picked accounts while guardUnsavedChanges() blocks the actual
     * server-side filter, leaving the visible selection out of sync — and a page
     * change would emit a page event whose reload discards the edits with no guard
     * in between, so the paginator is disabled outright.
     */
    get hasUnsavedChanges(): boolean {
        return this._hasUnsavedChanges();
    }

    onTabChange(event: MatTabChangeEvent): void {
        // Pivot is the second tab in the template.
        if (event.index !== 1) return;
        const seq = ++this.pivotLoadSeq;
        this.fullDatasetLoaded = false;
        this.pivotLoadFailed = false;
        this.loadPivotDataset(seq).catch(err => {
            // Ignore a superseded load's failure: the user re-entered the Pivot tab and a
            // newer load has taken over (or the component was destroyed). Letting a stale
            // rejection set the error state here would mask the newer load's result.
            if (this.isDestroyed || seq !== this.pivotLoadSeq) return;
            console.error('Failed to load pivot dataset', err);
            this.notification.openError('Failed to load pivot dataset. Please try again.');
            // Don't flip fullDatasetLoaded true on failure: the template would then render
            // <pivot-view> over stale data from a previous successful load (or an empty
            // pivot), reading as "loaded" right after we told the user it failed. Show an
            // explicit error state and drop any stale projection instead.
            this.pivotLoadFailed = true;
            this.pivotData = [];
        });
    }

    // seq defaults to the current token so direct callers (tests) aren't treated as stale;
    // onTabChange passes a freshly-incremented seq so only the latest load writes state.
    private async loadPivotDataset(seq: number = this.pivotLoadSeq): Promise<void> {
        // Intentional: Pivot aggregates over every mapper the user can access
        // (backend mapper reads are auto-scoped to all accessible accounts, not the
        // X-Active-Account header), regardless of the Table tab's search/account
        // filter. WIRE-14962 confirmed this as desired behaviour; do not pass
        // buildCurrentFilters() here.
        const {rows, hitCap} = await fetchAllPages<MapperRow>(this.api.mapper, {
            // Bail the paging loop early too once a newer load supersedes this one, not
            // just on destroy — no point fetching pages whose result the seq guard below
            // will discard.
            isCancelled: () => this.isDestroyed || seq !== this.pivotLoadSeq,
        });
        if (this.isDestroyed || seq !== this.pivotLoadSeq) return;
        if (hitCap) {
            this.notification.openError(
                'Pivot dataset load aborted: exceeded maximum page count. Showing partial data.'
            );
        }
        // Resolve referenced series so the pivot's Series column (projected via the
        // column accessor in PivotViewComponent) shows names rather than blanks. As with
        // download, a transient lookup failure prompts a one-shot retry and never blocks
        // the pivot — show it with blank names and flag the gap after.
        let seriesComplete = true;
        try {
            await firstValueFrom(this.fetchMissingSeries(rows, {confirmRetryOnError: true}));
        } catch (err) {
            console.warn('Series names could not be fully resolved for the pivot', err);
            seriesComplete = false;
        }
        if (this.isDestroyed || seq !== this.pivotLoadSeq) return;
        // Normalize attributes.json to a stable string (JSONB can arrive parsed) so the
        // pivot's JSON column groups/displays the same text as the table and export —
        // matching the page-load and download paths.
        this.formatMapperJson(rows);
        this.pivotData = rows;
        this.fullDatasetLoaded = true;
        if (!seriesComplete) {
            this.notification.openWarning('Pivot loaded, but some series names could not be loaded.');
        }
    }

    buildHeader() {
        this.headerData.title = 'Mappers';
        this.headerData.buttons = [
            {name: 'Save', func: this.save.bind(this), class: 'icon-save', params: {}},
            {name: 'Download', func: this.download.bind(this), class: 'icon-download', params: {}}
        ];
    }

    ngOnDestroy(): void {
        // isDestroyed gates the async download() and loadPivotDataset() loops;
        // RxJS subscriptions are torn down via the injected DestroyRef — except the
        // dataSource's internal paginator.page subscription, which it owns itself.
        this.isDestroyed = true;
        // Tear down the format-picker overlay if it's still open: the download()
        // subscriptions that would otherwise close it use takeUntilDestroyed, which
        // completes them without emitting on destroy, so neither handle.close() nor
        // the dismissal path runs — leaving the CDK overlay orphaned on screen.
        // close() is idempotent, so this is a no-op once the picker was picked/dismissed.
        this.downloadPopupHandle?.close();
        this.dataSource?.disconnect();
    }
}
